/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */

const { hostname } = window.location;
const subdomain = hostname.split(".")[0];

const names = {
  signIn: 'B2C_1_signinflow',
  forgotPassword: 'B2C_1_resetflow',
  editProfile: 'B2C_1_edit_profile',
}

if (subdomain === "homologation") {
  names.signIn = 'B2C_1_HML_signinflow';
} else if (subdomain === "qa" || subdomain === "dev") {
  names.signIn = 'B2C_1_QA_signinflow';
}

const b2cPolicies = {
  names,
  authorities: {
    signIn: {
      authority: `https://topconidentity.b2clogin.com/topconidentity.onmicrosoft.com/${names.signIn}`,
    },
    forgotPassword: {
      authority: `https://topconidentity.b2clogin.com/topconidentity.onmicrosoft.com/${names.forgotPassword}`,
    },
    editProfile: {
      authority: `https://topconidentity.b2clogin.com/topconidentity.onmicrosoft.com/${names.editProfile}`,
    },
  },
  authorityDomain: 'topconidentity.b2clogin.com',
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
const msalConfig = {
  auth: {
    clientId: '1d8965e0-2e10-45f0-a8dc-a6fb9ac06fc4', // This is the ONLY mandatory field that you need to supply.
    authority: b2cPolicies.authorities.signIn.authority, // Choose SUSI as your default authority.
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri: '/', // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
    postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            // console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

/**
 * Reserved for product APIs that expose explicit scopes in their B2C App
 * Registration. The Topcon suite runs under a single shared App Registration
 * (see msalConfig.auth.clientId), so for SSO between suite products we only
 * need OIDC scopes — the ID token carries every claim a legacy product
 * needs (sub, email, name, extension_TenantId, extension_Modules).
 *
 * Kept as an empty object so external integrations that *do* expose a custom
 * scope (e.g. a future third-party API) can be added here without changing
 * the export shape consumed by `useAuth.tsx`.
 */
const protectedResources = {};

/**
 * Scopes requested at sign-in. OIDC-only — see protectedResources for the
 * rationale. MSAL.js implicitly adds `openid` and `profile`; we list them
 * explicitly for clarity.
 */
const loginRequest = {
  scopes: ['openid', 'profile'],
};


export {
  b2cPolicies,
  msalConfig,
  protectedResources,
  loginRequest
};
